<script setup lang="ts">
/** 공통 Allottee 반복표 ([[if allottees]] 재현) — ctx 로 행 추가/삭제. 문서 전체가 allottees 배열을 공유.
 *  컬럼은 props 로 토글: idNo(NRIC/Reg)·shareType(Class)·amount(Capitalized RM).
 *  statementKeys: Statement 템플릿 키(allottee_shares_otherwise / allottee_amount_capitalized) 바인딩.
 *  (키 페어 미러링은 패널 setFieldValue 에서 처리 — 여기서 watch 하면 입력/Clear 가 깨짐.) */
import { inject, computed } from 'vue';
import { BONUS_DOC_CTX } from '../bonusContext';
import BonusDocVarField from '../BonusDocVarField.vue';

const props = withDefaults(defineProps<{
  sharesHeader?: string;
  showTotal?: boolean;
  showIdNo?: boolean;
  showShareType?: boolean;
  showAmount?: boolean;
  /** BON07 Statement — Word 키 allottee_shares_otherwise / allottee_amount_capitalized */
  statementKeys?: boolean;
}>(), {
  sharesHeader: 'No. of Shares Allotted',
  showTotal: true,
  showIdNo: false,
  showShareType: false,
  showAmount: false,
  statementKeys: false,
});

const ctx = inject(BONUS_DOC_CTX)!;
const rows = computed(() => Array.from({ length: ctx.allotteeCount.value }, (_, i) => i + 1));
const extraCols = computed(() => (props.showIdNo ? 1 : 0) + (props.showShareType ? 1 : 0) + (props.showAmount ? 1 : 0));
const baseCols = computed(() => 2 + extraCols.value + 1);
const leadCols = computed(() => 2 + (props.showIdNo ? 1 : 0) + (props.showShareType ? 1 : 0));

const num = (v: any) => Number(String(v ?? '').replace(/[^0-9.]/g, '')) || 0;
const amountKey = (n: number) =>
  props.statementKeys ? `allottee_amount_capitalized_${n}` : `allottee_amount_${n}`;
const sharesKey = (n: number) =>
  props.statementKeys ? `allottee_shares_otherwise_${n}` : `allottee_shares_${n}`;

const totalAmount = computed(() =>
  rows.value.reduce((s, n) => s + num(ctx.valueOf(amountKey(n)) || ctx.valueOf(`allottee_amount_${n}`)), 0),
);
const fmt = (v: number) => (v ? v.toLocaleString('en-US') : '');
</script>

<template>
  <table class="cdd-tbl">
    <tbody>
      <tr>
        <th style="width:38px">#</th>
        <th>Names of Allottee(s) <span class="cdd-cn">受配人姓名</span></th>
        <th v-if="showIdNo">NRIC / Reg. No.</th>
        <th v-if="showShareType">Class of Shares</th>
        <th>{{ sharesHeader }}</th>
        <th v-if="showAmount">Amount Capitalized (RM)</th>
        <th style="width:48px"></th>
      </tr>
      <tr v-for="(n, i) in rows" :key="n">
        <td>{{ i + 1 }}</td>
        <td>
          <BonusDocVarField :field-key="`allottee_name_${n}`" placeholder="Allottee Name" /><br />
          <BonusDocVarField :field-key="`allottee_address_${n}`" placeholder="Allottee Address" />
        </td>
        <td v-if="showIdNo"><BonusDocVarField :field-key="`allottee_id_no_${n}`" placeholder="NRIC / Reg. No." /></td>
        <td v-if="showShareType"><BonusDocVarField :field-key="`allottee_share_type_${n}`" placeholder="Class of Shares" /></td>
        <td>
          <BonusDocVarField
            :field-key="sharesKey(n)"
            :placeholder="statementKeys ? 'Shares Otherwise' : 'No. of Shares'"
          />
        </td>
        <td v-if="showAmount">
          <BonusDocVarField
            :field-key="amountKey(n)"
            placeholder="Amount Capitalized (RM)"
          />
        </td>
        <td>
          <button v-if="ctx.allotteeCount.value > 1" class="bon-rowbtn" title="Remove" @click="ctx.removeAllottee(i + 1)">✕</button>
        </td>
      </tr>
      <tr>
        <td :colspan="baseCols"><button class="bon-addrow" @click="ctx.addAllottee()">+ Add allottee</button></td>
      </tr>
      <tr v-if="showTotal">
        <td :colspan="leadCols" style="text-align:right"><b>Total</b></td>
        <td><BonusDocVarField field-key="total_new_shares" placeholder="Total New Shares" /></td>
        <td v-if="showAmount"><b>{{ fmt(totalAmount) }}</b></td>
        <td></td>
      </tr>
    </tbody>
  </table>
</template>
